import React, { useState, useEffect } from "react";
import { Navigation } from "./components/navigation";
import { Header } from "./components/header";
import { AgentShowcase } from "./components/agent-showcase";
import { Features } from "./components/features";
import { About } from "./components/about";
import { Services } from "./components/services";
import { Gallery } from "./components/gallery";
import { Testimonials } from "./components/testimonials";
import { GlobalPresence } from "./components/global-presence";
import { Contact } from "./components/contact";
import { CaseDetail } from "./components/case-detail";
import { CompanyDetail } from "./components/company-detail";
import { CapabilityCloud } from "./components/capability-cloud";
import { CloudLogin } from "./components/cloud-login";
import { Footer } from "./components/footer";
import JsonData from "./data/data.json";
import SmoothScroll from "smooth-scroll";
import "./App.css";

export const scroll = new SmoothScroll('a[href*="#"]', {
  speed: 1000,
  speedAsDuration: true,
});

const CASE_HASH_PREFIX = "#/case/";
const CLOUD_HASH = "#/cloud";
const CLOUD_LOGIN_HASH = "#/cloud/login";
const CLOUD_SESSION_KEY = "goldly-cloud-preview-session";
const COMPANY_PAGE_SLUGS = [
  "agent-platform",
  "global-presence",
  "team-expertise",
  "why-goldly",
];

const slugFromHash = () => {
  const h = window.location.hash || "";
  return h.startsWith(CASE_HASH_PREFIX)
    ? decodeURIComponent(h.slice(CASE_HASH_PREFIX.length))
    : null;
};

const companyPageFromHash = () => {
  const hash = window.location.hash || "";
  const slug = hash.startsWith("#/") ? hash.slice(2) : "";
  return COMPANY_PAGE_SLUGS.includes(slug) ? slug : null;
};

const hasCloudSession = () =>
  window.sessionStorage.getItem(CLOUD_SESSION_KEY) === "active";

const cloudFromHash = () =>
  window.location.hash === CLOUD_HASH && hasCloudSession();

const cloudLoginFromHash = () => {
  const hash = window.location.hash;
  return hash === CLOUD_LOGIN_HASH || (hash === CLOUD_HASH && !hasCloudSession());
};

const App = () => {
  const [landingPageData, setLandingPageData] = useState({});
  const [activeCaseSlug, setActiveCaseSlug] = useState(() => slugFromHash());
  const [activeCompanyPage, setActiveCompanyPage] = useState(() =>
    companyPageFromHash()
  );
  const [activeCloud, setActiveCloud] = useState(() => cloudFromHash());
  const [activeCloudLogin, setActiveCloudLogin] = useState(() =>
    cloudLoginFromHash()
  );

  useEffect(() => {
    setLandingPageData(JsonData);
  }, []);

  useEffect(() => {
    const onHash = () => {
      setActiveCaseSlug(slugFromHash());
      setActiveCompanyPage(companyPageFromHash());
      setActiveCloud(cloudFromHash());
      setActiveCloudLogin(cloudLoginFromHash());
      window.scrollTo(0, 0);
    };
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  const openCase = (slug) => {
    window.location.hash = CASE_HASH_PREFIX + slug;
  };

  const openCompanyPage = (slug) => {
    if (activeCompanyPage === slug) {
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    window.location.hash = `#/${slug}`;
  };

  const openCloud = () => {
    window.location.hash = hasCloudSession() ? CLOUD_HASH : CLOUD_LOGIN_HASH;
  };

  const enterCloud = () => {
    window.sessionStorage.setItem(CLOUD_SESSION_KEY, "active");
    window.history.replaceState(
      null,
      "",
      `${window.location.pathname}${window.location.search}${CLOUD_HASH}`
    );
    setActiveCloudLogin(false);
    setActiveCloud(true);
  };

  const leaveCloud = () => {
    window.sessionStorage.removeItem(CLOUD_SESSION_KEY);
    window.history.replaceState(
      null,
      "",
      `${window.location.pathname}${window.location.search}${CLOUD_LOGIN_HASH}`
    );
    setActiveCloud(false);
    setActiveCloudLogin(true);
  };

  const returnHome = (section = "page-top") => {
    window.history.replaceState(
      null,
      "",
      `${window.location.pathname}${window.location.search}#${section}`
    );
    setActiveCaseSlug(null);
    setActiveCompanyPage(null);
    setActiveCloud(false);
    setActiveCloudLogin(false);
    window.requestAnimationFrame(() => {
      document
        .getElementById(section)
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  const cases = landingPageData.Testimonials || [];
  const activeCase = activeCaseSlug
    ? cases.find((c) => c.slug === activeCaseSlug)
    : null;

  if (activeCloudLogin) {
    return <CloudLogin onBack={returnHome} onLogin={enterCloud} />;
  }

  if (activeCloud) {
    return <CapabilityCloud onHome={returnHome} onLogout={leaveCloud} />;
  }

  if (activeCase) {
    return (
      <>
        <CaseDetail
          data={activeCase}
          allCases={cases}
          onBack={returnHome}
          onOpenCase={openCase}
        />
        <Footer contact={landingPageData.Contact} onHome={returnHome} />
      </>
    );
  }

  if (activeCompanyPage) {
    return (
      <CompanyDetail
        page={activeCompanyPage}
        data={landingPageData}
        onHome={returnHome}
        onOpenPage={openCompanyPage}
      />
    );
  }

  return (
    <div>
      <Navigation onOpenCloud={openCloud} />
      <Header data={landingPageData.Header} />
      <AgentShowcase
        data={landingPageData.AgentPlatform}
        onOpenPage={openCompanyPage}
      />
      <Features data={landingPageData.Features} />
      <About data={landingPageData.About} />
      <Services data={landingPageData.Services} />
      <Gallery data={landingPageData.Gallery} />
      <Testimonials data={landingPageData.Testimonials} onOpenCase={openCase} />
      <GlobalPresence
        data={landingPageData.GlobalPresence}
        onOpenPage={openCompanyPage}
      />
      <Contact data={landingPageData.Contact} />
      <Footer contact={landingPageData.Contact} />
    </div>
  );
};

export default App;
